/**
 * `usePeopleCapabilities` — fetches `/api/people/capabilities` so the
 * `/people` screen and rail render data-driven (who may read / create / edit /
 * deactivate / reset PIN, and over which unit scope) instead of branching
 * on `role === "admin"`. The endpoint answers 200 with empty caps for a
 * non-manager, so the hook has no error branch to special-case.
 */
import { queryOptions, useQuery } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import { getPeopleCapabilities, type PeopleCapabilities } from "@/api/admin";

export const PEOPLE_CAPABILITIES_KEY = ["people-capabilities"] as const;

function noRetryOn401(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return false;
  }
  return failureCount < 1;
}

export const peopleCapabilitiesQueryOptions = queryOptions<PeopleCapabilities, Error>({
  queryKey: PEOPLE_CAPABILITIES_KEY,
  queryFn: getPeopleCapabilities,
  retry: noRetryOn401,
  staleTime: 60_000,
});

export function usePeopleCapabilities() {
  return useQuery(peopleCapabilitiesQueryOptions);
}
